import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Card, Box, Typography, Avatar } from '@mui/material';
import {
  GroupOutlined,
  BadgeOutlined,
  AccountCircleOutlined,
} from '@mui/icons-material';

/**
 * Individual metric card component
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {string|number} props.value - Main metric value
 * @param {string} props.subtitle - Additional info text
 * @param {React.ReactNode} props.icon - Icon component to display
 * @param {string} props.color - Theme color for styling
 * @returns {React.Component} Styled metric card
 */
function MetricCard({ title, value, subtitle, icon, color = 'primary' }) {
  return (
    <Card sx={{ height: '100%' }}>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `${color}.light`,
              color: `${color}.main`,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </Box>
    </Card>
  );
}

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.node.isRequired,
  color: PropTypes.string,
};

/**
 * Metrics cards grid displaying key people management statistics
 * @param {Object} props - Component props
 * @param {Object} props.metrics - Metrics data object
 * @param {number} props.metrics.totalEmployees - Total employee count
 * @param {number} props.metrics.activeEmployees - Active employee count
 * @param {number} props.metrics.totalRoleDefinitions - Total role definitions
 * @param {number} props.metrics.activeRoleDefinitions - Active role definitions
 * @param {number} props.metrics.totalUsers - Total system users
 * @param {number} props.metrics.usersWithEmployee - Users linked to employees
 * @param {number} props.metrics.systemAccessCoverage - Percentage of coverage
 * @returns {React.Component} Grid of metric cards
 */
function MetricsCards({ metrics }) {
  const cards = [
    {
      title: 'Total Employees',
      value: metrics.totalEmployees || 0,
      subtitle: `${metrics.activeEmployees || 0} active`,
      icon: <GroupOutlined />,
      color: 'primary',
    },
    {
      title: 'Role Definitions',
      value: metrics.totalRoleDefinitions || 0,
      subtitle: `${metrics.activeRoleDefinitions || 0} active`,
      icon: <BadgeOutlined />,
      color: 'secondary',
    },
    {
      title: 'System Users',
      value: metrics.totalUsers || 0,
      subtitle: `${metrics.usersWithEmployee || 0} linked`,
      icon: <AccountCircleOutlined />,
      color: 'info',
    },
  ];

  return (
    <Grid container spacing={3}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <MetricCard {...card} />
        </Grid>
      ))}
    </Grid>
  );
}

MetricsCards.propTypes = {
  metrics: PropTypes.shape({
    totalEmployees: PropTypes.number,
    activeEmployees: PropTypes.number,
    totalRoleDefinitions: PropTypes.number,
    activeRoleDefinitions: PropTypes.number,
    totalUsers: PropTypes.number,
    usersWithEmployee: PropTypes.number,
    systemAccessCoverage: PropTypes.number,
  }).isRequired,
};

export default MetricsCards;